/**
 * Adding a kind of asset (BR-09, D-018).
 *
 * Admin-only, because the life set here is the life every asset filed under
 * this category will depreciate over — it is a decision about the books, not a
 * preference. It sits inside whichever form needed it rather than on a
 * settings screen, because the moment a partner discovers the category is
 * missing is the moment they are trying to record something, and sending them
 * elsewhere is how an ৳80,000 pump ends up filed as a month's expense.
 *
 * Its own module because two screens now open it — the asset register and the
 * setup-costs form. A second copy would be a second place for the life rules
 * to drift.
 */

import { useState } from 'react';

import { useTranslation } from '../../i18n/useTranslation.js';
import { errorMessageKey } from '../../lib/apiError.js';
import { useCreateAssetCategory, type AssetCategory } from './api.js';

export function NewCategoryForm({ onAdded }: { onAdded: (category: AssetCategory) => void }): JSX.Element {
  const { t } = useTranslation();
  const createCategory = useCreateAssetCategory();

  const [name, setName] = useState('');
  const [years, setYears] = useState('');
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const life = Number(years);
  const canSubmit = name.trim().length >= 2 && Number.isInteger(life) && life >= 1 && life <= 50;

  async function submit(): Promise<void> {
    setErrorKey(null);

    try {
      const created = await createCategory.mutateAsync({ name: name.trim(), usefulLifeYears: life });
      setName('');
      setYears('');
      onAdded(created);
    } catch (error) {
      setErrorKey(errorMessageKey(error));
    }
  }

  return (
    <div className="mt-3 rounded-lg bg-gray-50 p-3">
      <div className="flex flex-wrap gap-2">
        <input
          aria-label={t('finance.assets.newCategoryName')}
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder={t('finance.assets.newCategoryName')}
          maxLength={60}
          className="min-h-touch flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm"
        />
        <input
          aria-label={t('finance.assets.newCategoryYears')}
          inputMode="numeric"
          value={years}
          onChange={(event) => setYears(event.target.value)}
          placeholder={t('finance.assets.newCategoryYears')}
          className="min-h-touch w-28 rounded-lg border border-gray-300 px-3 py-2 text-sm tabular-nums"
        />
        <button
          type="button"
          onClick={() => void submit()}
          disabled={!canSubmit || createCategory.isPending}
          className="min-h-touch rounded-lg bg-green-700 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {t('finance.assets.addCategory')}
        </button>
      </div>

      {errorKey !== null && (
        <div role="alert" className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800">
          <p>{t(errorKey)}</p>
        </div>
      )}
    </div>
  );
}
